import type { AssessCandidateResponse } from "@shared/labs/candidate-assessment";

const STORAGE_PREFIX = "labs-ci-result:";
const URL_PARAM = "id";
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface PersistedAssessment {
  result: AssessCandidateResponse;
  linkedinUrl: string;
  discipline: string;
  positionContext: string;
  savedAt: number;
}

export function readIdFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const id = new URLSearchParams(window.location.search).get(URL_PARAM);
  return id && /^[a-f0-9-]{10,}$/i.test(id) ? id : null;
}

export function writeIdToUrl(id: string): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set(URL_PARAM, id);
  window.history.replaceState({}, "", url.toString());
}

export function clearIdFromUrl(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(URL_PARAM)) return;
  url.searchParams.delete(URL_PARAM);
  window.history.replaceState({}, "", url.toString());
}

export function loadAssessment(id: string): PersistedAssessment | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + id);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedAssessment;
    if (Date.now() - parsed.savedAt > TTL_MS) {
      window.localStorage.removeItem(STORAGE_PREFIX + id);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveAssessment(id: string, data: Omit<PersistedAssessment, "savedAt">): void {
  try {
    const payload: PersistedAssessment = { ...data, savedAt: Date.now() };
    window.localStorage.setItem(STORAGE_PREFIX + id, JSON.stringify(payload));
  } catch {
    // Quota exceeded or serialization failure — silently drop; the URL param
    // will still exist but the reload will fall back to the form.
  }
}

export function clearAssessment(id: string): void {
  try {
    window.localStorage.removeItem(STORAGE_PREFIX + id);
  } catch {
    // ignore
  }
}

export function newAssessmentId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}
